import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, switchMap, tap, catchError, of, shareReplay } from 'rxjs';
import { EquipamentoService } from '@core/services/domain-services';
import { Equipamento } from '@core/models/models';
import { matchesTipoEspecifico } from '@shared/utils/equipamento.utils';

export interface EquipamentoFilters {
  Estado?: string | null;
  Ativo?: boolean | null;
  IdPosto?: number | null;
  DataAquisicao?: string | Date | null;
  Search?: string | null; // CodigoInterno or general search
  IdTipoEquipamento?: number | null;
  TipoEquipamentoEnum?: string | null;
}

/** TipoEquipamentoEnum represents the API's broad category enum, not concrete types such as FUZIL. */
export function filtrosApiEquipamentos(filtros: EquipamentoFilters): Record<string, unknown> {
  const { TipoEquipamentoEnum: _tipoEspecifico, ...apiFilters } = filtros;
  return apiFilters;
}

/** Applies the drawer filters locally as a fallback when the API ignores a query parameter. */
export function filtrarEquipamentos(
  equipamentos: Equipamento[],
  filtros: EquipamentoFilters,
): Equipamento[] {
  const normalizar = (valor: unknown) => String(valor ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]/g, '');
  const data = (valor: unknown) => {
    if (!valor) return '';
    if (valor instanceof Date && !Number.isNaN(valor.getTime())) {
      const ano = valor.getFullYear();
      const mes = String(valor.getMonth() + 1).padStart(2, '0');
      const dia = String(valor.getDate()).padStart(2, '0');
      return `${ano}-${mes}-${dia}`;
    }
    const texto = String(valor).trim();
    const dataIso = texto.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dataIso) return dataIso[1];
    const dataLocal = texto.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (dataLocal) {
      return `${dataLocal[3]}-${dataLocal[2].padStart(2, '0')}-${dataLocal[1].padStart(2, '0')}`;
    }
    const parsed = new Date(texto);
    return Number.isNaN(parsed.getTime()) ? texto : parsed.toISOString().slice(0, 10);
  };

  return equipamentos.filter((equipamento) => {
    if (filtros.IdTipoEquipamento != null && equipamento.idTipoEquipamento !== Number(filtros.IdTipoEquipamento)) return false;
    if (filtros.TipoEquipamentoEnum && !matchesTipoEspecifico(equipamento, filtros.TipoEquipamentoEnum)) return false;
    if (filtros.Estado != null && normalizar(equipamento.estado) !== normalizar(filtros.Estado)) return false;
    if (filtros.Ativo != null && equipamento.ativo !== filtros.Ativo) return false;
    if (filtros.IdPosto != null && Number(equipamento.idPosto) !== Number(filtros.IdPosto)) return false;
    const equipamentoRaw = equipamento as any;
    const dataAquisicao = equipamento.dataAquisicao ?? equipamentoRaw.DataAquisicao;
    if (filtros.DataAquisicao && data(dataAquisicao) !== data(filtros.DataAquisicao)) return false;
    if (filtros.Search) {
      const termo = normalizar(filtros.Search);
      const item = equipamento as any;
      const propriedades = item.propriedades ?? item.Propriedades ?? [];
      const valorPropriedade = (...nomes: string[]) => {
        const propriedade = propriedades.find((p: any) =>
          nomes.includes(String(p?.nome ?? p?.Nome ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase()),
        );
        return propriedade?.valorDescricao ?? propriedade?.ValorDescricao ?? propriedade?.valor ?? propriedade?.Valor;
      };
      const campos = [
        equipamento.codigoInterno,
        item.CodigoInterno,
        equipamento.numeroDeSerie,
        item.NumeroDeSerie,
        item.matricula,
        item.Matricula,
        item.MATRICULA,
        item.placa,
        item.Placa,
        item.PLACA,
        valorPropriedade('MATRICULA', 'PLACA'),
        valorPropriedade('NOME', 'NOME_ITEM', 'DESIGNACAO'),
        equipamento.marca,
        equipamento.modelo,
        equipamento.tipoEquipamento?.designacao,
        equipamento.posto?.nome,
      ];
      if (!campos.some((campo) => normalizar(campo).includes(termo))) return false;
    }
    return true;
  });
}

@Injectable({
  providedIn: 'root',
})
export class EquipamentoFilterStateService {
  private readonly equipamentoSvc = inject(EquipamentoService);

  private filtersSubject = new BehaviorSubject<EquipamentoFilters>({});
  public filters$: Observable<EquipamentoFilters> = this.filtersSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  private totalRecordsSubject = new BehaviorSubject<number>(0);
  public totalRecords$ = this.totalRecordsSubject.asObservable();

  // The main unified list of all equipamentos, fetched whenever filters change
  public equipamentos$: Observable<Equipamento[]> = this.filters$.pipe(
    tap(() => this.loadingSubject.next(true)),
    switchMap(filters => {
      const params: Record<string, unknown> = {
        pageNumber: 1,
        pageSize: 1000, // Fetch a large number to ensure we get all records for distribution
        ...filters
      };
      return this.equipamentoSvc.getAll(params).pipe(
        tap(res => this.totalRecordsSubject.next(res.totalRecords ?? 0)),
        map(res => res.data ?? []),
        catchError(() => {
          this.totalRecordsSubject.next(0);
          return of([] as Equipamento[]);
        })
      );
    }),
    tap(() => this.loadingSubject.next(false)),
    shareReplay(1) // Cache the latest result so late subscribers (tabs) get the data immediately
  );

  // Derived lists for each tab
  public armas$: Observable<Equipamento[]> = this.equipamentos$.pipe(
    map(list => list.filter(e => e.idTipoEquipamento === 1))
  );

  public viaturas$: Observable<Equipamento[]> = this.equipamentos$.pipe(
    map(list => list.filter(e => e.idTipoEquipamento === 2))
  );

  public utensilios$: Observable<Equipamento[]> = this.equipamentos$.pipe(
    map(list => list.filter(e => e.idTipoEquipamento === 3))
  );

  get currentFilters(): EquipamentoFilters {
    return this.filtersSubject.value;
  }

  updateFilters(filters: EquipamentoFilters): void {
    this.filtersSubject.next({ ...this.filtersSubject.value, ...filters });
  }

  setFilters(filters: EquipamentoFilters): void {
    this.filtersSubject.next(filters);
  }

  clearFilters(): void {
    this.filtersSubject.next({});
  }
}
